import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, MapPin, Store, Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { BusinessSubmitDialog } from "@/components/BusinessSubmitDialog";
import { usePageMeta } from "@/hooks/usePageMeta";
import { api, fileUrl } from "@/lib/api";

const AREA_LABELS = { local: "Local", online: "Online", nationwide: "Nationwide" };

export default function Directory() {
  usePageMeta("Kingdom Business Directory",
    "Find businesses that share your faith. Search and support Christ-centered businesses in your community and online.");
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitOpen, setSubmitOpen] = useState(params.get("submit") === "1");
  const [demo, setDemo] = useState(false);

  const [q, setQ] = useState("");
  const [category, setCategory] = useState(params.get("category") || "");
  const [serviceArea, setServiceArea] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.categories)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = { demo };
      if (q) p.q = q;
      if (category) p.category = category;
      if (serviceArea) p.service_area = serviceArea;
      if (city) p.city = city;
      if (state) p.state = state;
      const r = await api.get("/businesses", { params: p });
      setItems(r.data.businesses);
    } finally { setLoading(false); }
  }, [q, category, serviceArea, city, state, demo]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const clearFilters = () => { setQ(""); setCategory(""); setServiceArea(""); setCity(""); setState(""); };
  const hasFilters = q || category || serviceArea || city || state;

  return (
    <div>
      <PageHero
        eyebrow="Kingdom Business Directory"
        title="Find Businesses That Share Your Faith."
        subtitle="Supporting Christ-centered businesses helps strengthen our communities and equips business owners to serve, give, and contribute to the advancement of God's Kingdom."
        image="https://images.unsplash.com/photo-1753351052617-62818ffc9173?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxjaHJpc3RpYW4lMjBidXNpbmVzcyUyMG93bmVyc3xlbnwwfHx8fDE3OTA2MDYzNzN8MA&ixlib=rb-4.1.0&q=85"
      />

      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
          <p className="text-sm text-muted-foreground max-w-2xl">
            Directory inclusion is not a guarantee of services or an endorsement of every business
            practice. Statements about faith and community involvement are provided by business owners.
          </p>
          <div className="flex items-center gap-4 shrink-0">
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Switch checked={demo} onCheckedChange={setDemo} data-testid="demo-toggle" />
              Preview demo listings
            </label>
            <Button onClick={() => setSubmitOpen(true)} className="rounded-full bg-emerald hover:bg-emerald-dark text-white px-6" data-testid="open-submit-business">
              Submit Your Business
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            <div className="relative lg:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search name, keyword…" value={q} onChange={(e) => setQ(e.target.value)}
                className="pl-9 bg-white border-border focus-visible:ring-emerald" data-testid="directory-search" />
            </div>
            <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
              <SelectTrigger className="bg-white border-border focus:ring-emerald" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={serviceArea || "all"} onValueChange={(v) => setServiceArea(v === "all" ? "" : v)}>
              <SelectTrigger className="bg-white border-border focus:ring-emerald" data-testid="filter-area"><SelectValue placeholder="Service area" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Local, online & nationwide</SelectItem>
                {Object.entries(AREA_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-2 lg:col-span-1">
              <Input placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} className="bg-white border-border focus-visible:ring-emerald" data-testid="filter-city" />
              <Input placeholder="State" value={state} onChange={(e) => setState(e.target.value)} className="bg-white border-border focus-visible:ring-emerald" data-testid="filter-state" />
            </div>
          </div>
          {hasFilters && (
            <button onClick={clearFilters} className="mt-3 inline-flex items-center gap-1 text-xs text-emerald hover:underline" data-testid="clear-filters">
              <X className="h-3 w-3" /> Clear filters
            </button>
          )}
        </div>

        {/* Category chips */}
        <div className="mt-6 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button key={c} onClick={() => setCategory(category === c ? "" : c)}
              className={`text-xs rounded-full px-4 py-1.5 border transition-colors ${
                category === c ? "bg-navy text-ivory border-navy" : "bg-white text-navy/70 border-border hover:border-gold"
              }`}>
              {c}
            </button>
          ))}
        </div>

        {/* Results */}
        <div className="mt-10">
          {loading ? (
            <p className="text-center text-muted-foreground py-16" data-testid="directory-loading">Loading…</p>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gold/40 bg-white/60 p-14 text-center" data-testid="directory-empty">
              <Store className="mx-auto h-10 w-10 text-gold" />
              <h3 className="mt-4 font-display text-2xl text-navy">
                {hasFilters ? "No businesses match your search" : "The directory is just getting started"}
              </h3>
              <p className="mt-2 text-muted-foreground max-w-lg mx-auto">
                {hasFilters
                  ? "Try adjusting or clearing your filters."
                  : "As Christ-centered businesses are submitted and approved, they will appear here. Own a business? Submit it above — listings are free."}
              </p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="directory-results">
              {items.map((b, i) => (
                <Reveal key={b.id} delay={(i % 3) * 0.06}>
                  <Link to={`/directory/${b.id}`} data-testid={`business-card-${b.id}`}
                    className="group flex h-full flex-col rounded-xl bg-white border border-border overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-1 transition-transform">
                    <div className="h-36 bg-ivory-alt flex items-center justify-center overflow-hidden">
                      {b.logo_url ? (
                        <img src={fileUrl(b.logo_url)} alt={b.name} className="h-full w-full object-cover" />
                      ) : (
                        <span className="font-display text-3xl text-navy/25">{b.name.replace(/^\[DEMO\]\s*/, "").charAt(0)}</span>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="border-emerald/30 text-emerald text-[11px]">{b.category}</Badge>
                        {b.featured && <Badge className="bg-gold/15 text-gold-dark border-none text-[11px]">Featured</Badge>}
                        {b.is_demo && <Badge className="bg-navy/10 text-navy border-none text-[11px]">Demo</Badge>}
                      </div>
                      <h3 className="mt-3 font-display text-2xl font-semibold text-navy group-hover:text-emerald transition-colors">{b.name}</h3>
                      <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{b.description}</p>
                      <div className="mt-4 flex items-center gap-1.5 text-xs text-navy/60">
                        <MapPin className="h-3.5 w-3.5" /> {b.city}, {b.state}
                        {b.service_areas?.length > 0 && <span className="ml-1">· {b.service_areas.map((s) => AREA_LABELS[s]).join(", ")}</span>}
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <BusinessSubmitDialog open={submitOpen} onOpenChange={setSubmitOpen} categories={categories} />
    </div>
  );
}
